import React from 'react';
import { Autocomplete, TextField, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { VehicleUsageType } from '../../types/vehicle';

interface VehicleFilterProps {
  vehicles: Array<{ id: string; name: string }>;
  fleets?: Array<{ id: string; name: string }>;
  selectedVehicle: string | null;
  selectedUsageType?: VehicleUsageType | null;
  selectedFleet?: string | null;
  onVehicleChange: (vehicleId: string | null) => void;
  onUsageTypeChange?: (usageType: VehicleUsageType | null) => void;
  onFleetChange?: (fleetId: string | null) => void;
}

const VehicleFilter = ({
  vehicles,
  fleets = [],
  selectedVehicle,
  selectedUsageType = null,
  selectedFleet = null,
  onVehicleChange,
  onUsageTypeChange = () => {},
  onFleetChange = () => {},
}: VehicleFilterProps): JSX.Element => {
  const { t } = useTranslation();

  const usageTypes: VehicleUsageType[] = ['SERVICE', 'COMPANY'];

  return (
    <Stack direction="row" spacing={2}>
      {onFleetChange && fleets.length > 0 && (
        <Autocomplete<{ id: string; name: string }>
          options={fleets}
          getOptionLabel={(option) => option.name}
          value={fleets.find((f) => f.id === selectedFleet) || null}
          onChange={(_, value) => onFleetChange(value?.id || null)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('vehicle.fleet')}
              variant="outlined"
              size="small"
              sx={{ minWidth: 200 }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText={t('vehicle.noFleet')}
          includeInputInList
          blurOnSelect
        />
      )}
      <Autocomplete<{ id: string; name: string }>
        options={vehicles}
        getOptionLabel={(option) => option.name}
        value={vehicles.find((v) => v.id === selectedVehicle) || null}
        onChange={(_, value) => onVehicleChange(value?.id || null)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('maintenance.filters.vehicle')}
            variant="outlined"
            size="small"
            sx={{ minWidth: 300 }}
          />
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        groupBy={() => t('maintenance.filters.vehicle')}
        noOptionsText={t('common.noOptions')}
        includeInputInList
        blurOnSelect
      />
      {onUsageTypeChange && (
        <Autocomplete<VehicleUsageType>
          options={usageTypes}
          value={selectedUsageType || null}
          onChange={(_, value) => onUsageTypeChange(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('vehicle.usage.title')}
              variant="outlined"
              size="small"
              sx={{ minWidth: 200 }}
            />
          )}
          getOptionLabel={(option) => t(`vehicle.usage.${option}`)}
          noOptionsText={t('common.noOptions')}
          includeInputInList
          blurOnSelect
        />
      )}
    </Stack>
  );
};

export default VehicleFilter;
